import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { enterpriseSsoAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { Fingerprint, Save, Copy, Plus, Trash2, Loader2, KeyRound, ShieldCheck } from 'lucide-react';
import Swal from 'sweetalert2';
import { EmptyState } from './common/States';

const ROLES = ['employee', 'manager', 'hr_manager', 'hr_admin', 'workspace_admin'];

const CopyField = ({ label, value }) => (
  <div className="space-y-1">
    <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
    <div className="flex items-center gap-2">
      <Input readOnly value={value || ''} className="font-mono text-xs" />
      <Button variant="outline" size="icon" type="button" title="Copy"
        onClick={() => { navigator.clipboard?.writeText(value || ''); Swal.fire({ icon: 'success', title: 'Copied', timer: 800, showConfirmButton: false }); }}>
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

const EnterpriseSsoSettings = () => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [tokens, setTokens] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([enterpriseSsoAPI.getSso(), enterpriseSsoAPI.listScimTokens()]);
      setCfg({ ...s.data, allowed_domains_text: (s.data.allowed_domains || []).join(', ') });
      setTokens(t.data || []);
    } catch (err) {
      if (err?.response?.status === 402) setCfg(null); // module not licensed
      else Swal.fire('Could not load SSO settings', err?.response?.data?.detail || 'Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (cfg.enabled && (!cfg.idp_sso_url || !cfg.idp_x509_cert)) {
      return Swal.fire('Incomplete', 'An IdP SSO URL and signing certificate are required before enabling SSO.', 'warning');
    }
    setSaving(true);
    try {
      const payload = {
        enabled: !!cfg.enabled,
        idp_entity_id: cfg.idp_entity_id || null,
        idp_sso_url: cfg.idp_sso_url || null,
        idp_x509_cert: cfg.idp_x509_cert || null,
        sp_entity_id: cfg.sp_entity_id_override || null,
        attr_email: cfg.attr_email || 'email',
        attr_first_name: cfg.attr_first_name || 'firstName',
        attr_last_name: cfg.attr_last_name || 'lastName',
        auto_provision: !!cfg.auto_provision,
        default_role: cfg.default_role || 'employee',
        allowed_domains: (cfg.allowed_domains_text || '').split(',').map((d) => d.trim()).filter(Boolean),
      };
      const res = await enterpriseSsoAPI.updateSso(payload);
      setCfg({ ...res.data, allowed_domains_text: (res.data.allowed_domains || []).join(', ') });
      Swal.fire({ icon: 'success', title: 'SSO settings saved', timer: 1300, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const generateToken = async () => {
    const { value: name } = await Swal.fire({
      title: 'New SCIM token', input: 'text', inputLabel: 'Label (e.g. "Okta production")',
      inputPlaceholder: 'Okta production', showCancelButton: true, confirmButtonText: 'Generate',
    });
    if (!name) return;
    try {
      const res = await enterpriseSsoAPI.createScimToken(name);
      await load();
      await Swal.fire({
        icon: 'success', title: 'Token created — copy it now',
        html: `<p class="text-sm">This token is shown only once. Paste it into your IdP's SCIM configuration.</p>
               <textarea readonly style="width:100%;margin-top:8px;font-family:monospace;font-size:12px;padding:8px" rows="3">${res.data.token}</textarea>
               <p class="text-xs" style="margin-top:8px">SCIM base URL: <code>${res.data.scim_base_url}</code></p>`,
        confirmButtonText: 'Copied',
      });
    } catch (err) {
      Swal.fire('Could not create token', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const revokeToken = async (t) => {
    const ok = await Swal.fire({ icon: 'warning', title: 'Revoke token?', text: t.name, showCancelButton: true, confirmButtonText: 'Revoke', confirmButtonColor: '#b5456b' });
    if (!ok.isConfirmed) return;
    try { await enterpriseSsoAPI.revokeScimToken(t.id); await load(); }
    catch (err) { Swal.fire('Revoke failed', err?.response?.data?.detail || 'Please try again.', 'error'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>;
  }
  if (!cfg) {
    return <EmptyState icon={ShieldCheck} title="SSO & Provisioning is not enabled for this workspace"
      description="This is an enterprise add-on. Contact your administrator to enable the SSO module." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Fingerprint className="h-6 w-6 text-indigo-600" />
        <div>
          <h2 className="text-2xl font-semibold">SSO &amp; Provisioning</h2>
          <p className="text-sm text-muted-foreground">SAML single sign-on and SCIM auto-provisioning for your identity provider.</p>
        </div>
      </div>

      {/* Service Provider details — paste these into the IdP */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Service Provider details</CardTitle>
          <CardDescription>Give these to your IdP admin (Okta, Azure AD, OneLogin, …).</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <CopyField label="SP Entity ID" value={cfg.sp_entity_id} />
          <CopyField label="ACS URL (Reply URL)" value={cfg.acs_url} />
          <CopyField label="SP Metadata URL" value={cfg.sp_metadata_url} />
          <CopyField label="Login URL (SP-initiated)" value={cfg.login_url} />
          <CopyField label="SCIM Base URL" value={cfg.scim_base_url} />
        </CardContent>
      </Card>

      {/* SAML configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">SAML configuration</CardTitle>
          <CardDescription>Details from your IdP's SAML application.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="font-medium flex items-center gap-2">SSO enabled {cfg.enabled && <Badge className="bg-emerald-600">Live</Badge>}</div>
              <div className="text-xs text-muted-foreground">When on, users can sign in through your IdP.</div>
            </div>
            <Switch checked={!!cfg.enabled} onCheckedChange={(v) => set('enabled', v)} disabled={!canManage} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label>IdP Entity ID / Issuer</Label>
              <Input value={cfg.idp_entity_id || ''} onChange={(e) => set('idp_entity_id', e.target.value)} placeholder="http://www.okta.com/exk…" disabled={!canManage} />
            </div>
            <div className="space-y-1">
              <Label>IdP SSO URL *</Label>
              <Input value={cfg.idp_sso_url || ''} onChange={(e) => set('idp_sso_url', e.target.value)} placeholder="https://acme.okta.com/app/…/sso/saml" disabled={!canManage} />
            </div>
          </div>

          <div className="space-y-1">
            <Label>IdP signing certificate (X.509) *</Label>
            <Textarea rows={5} value={cfg.idp_x509_cert || ''} onChange={(e) => set('idp_x509_cert', e.target.value)}
              placeholder={'-----BEGIN CERTIFICATE-----\n…\n-----END CERTIFICATE-----'} className="font-mono text-xs" disabled={!canManage} />
            <p className="text-xs text-muted-foreground">Every login assertion is signature-verified against this certificate.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-1"><Label>Email attribute</Label><Input value={cfg.attr_email || ''} onChange={(e) => set('attr_email', e.target.value)} placeholder="email" disabled={!canManage} /></div>
            <div className="space-y-1"><Label>First-name attribute</Label><Input value={cfg.attr_first_name || ''} onChange={(e) => set('attr_first_name', e.target.value)} placeholder="firstName" disabled={!canManage} /></div>
            <div className="space-y-1"><Label>Last-name attribute</Label><Input value={cfg.attr_last_name || ''} onChange={(e) => set('attr_last_name', e.target.value)} placeholder="lastName" disabled={!canManage} /></div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Allowed email domains</Label>
              <Input value={cfg.allowed_domains_text || ''} onChange={(e) => set('allowed_domains_text', e.target.value)} placeholder="acme.com, acme.co.uk" disabled={!canManage} />
              <p className="text-xs text-muted-foreground">Comma-separated. Leave blank to allow any domain.</p>
            </div>
            <div className="space-y-1">
              <Label>Default role for new users</Label>
              <Select value={cfg.default_role || 'employee'} onValueChange={(v) => set('default_role', v)} disabled={!canManage}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="font-medium">Auto-provision users (JIT)</div>
              <div className="text-xs text-muted-foreground">Create a user on first SSO login. Off = only pre-existing users may sign in.</div>
            </div>
            <Switch checked={!!cfg.auto_provision} onCheckedChange={(v) => set('auto_provision', v)} disabled={!canManage} />
          </div>

          {canManage && (
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save configuration</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SCIM provisioning tokens */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4" /> SCIM provisioning tokens</CardTitle>
            <CardDescription>Bearer tokens your IdP uses to create/deactivate users automatically.</CardDescription>
          </div>
          {canManage && <Button onClick={generateToken}><Plus className="h-4 w-4 mr-1" /> Generate token</Button>}
        </CardHeader>
        <CardContent>
          {tokens.length === 0 ? (
            <EmptyState icon={KeyRound} title="No SCIM tokens yet" description="Generate a token and paste it into your IdP's provisioning settings." />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Token</TableHead><TableHead>Status</TableHead>
                    <TableHead>Last used</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tokens.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell className="font-mono text-xs">{t.token_prefix}…</TableCell>
                      <TableCell>{t.enabled ? <Badge className="bg-emerald-600">Active</Badge> : <Badge variant="secondary">Revoked</Badge>}</TableCell>
                      <TableCell>{t.last_used_at ? new Date(t.last_used_at).toLocaleString() : '—'}</TableCell>
                      <TableCell className="text-right">
                        {canManage && t.enabled && <Button variant="ghost" size="icon" onClick={() => revokeToken(t)} title="Revoke"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EnterpriseSsoSettings;
